import { Component, Inject, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialog,
} from '@angular/material/dialog';
import { HttpClient, HttpEvent, HttpEventType } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { environment } from 'src/environments/environment';
import { ItemLibraryService, ItemLibraryModel } from 'src/app/Services/item-library.service';
import { ItemDetailsDialogComponent } from '../item-details-dialog/item-details-dialog.component';
import { AddNewItemDialogComponent } from '../add-new-item-dialog/add-new-item-dialog.component';
import { debounceTime, distinctUntilChanged, switchMap, catchError } from 'rxjs/operators';
import { of, Subject } from 'rxjs';

export interface CaptionDialogData {
  title: string;
  description: string;
  type: 'cover' | 'step';
  stepIndex?: number;
  isVideo?: boolean;
  posterUrl?: string;
  mediaUrl?: string;
  taggedItems?: any[];
  itemGroupList?: string[];
  userData?: any;
  inames?: any[];
  supportingDocumentDocuments?: any[];
  supportingDocumentHyperLinks?: any[];
  isSafetyCritical?: boolean;
  safetyInstructionsTitle?: string;
  safetyInstructionsDescription?: string;
}

@Component({
  selector: 'app-caption-dialog',
  templateUrl: './caption-dialog.component.html',
  styleUrls: ['./caption-dialog.component.scss']
})
export class CaptionDialogComponent implements OnInit, OnDestroy {
  title: string = '';
  description: string = '';
  maxtitlelenght = 80;
  maxdesclenght = 500;
  
  // Cover image upload properties
  coverImageFile: File | null = null;
  coverImageUrl: string = '';
  isUploadingCoverImage: boolean = false;
  coverImageUploadProgress: number = 0;

  // Tagged items properties (only for steps)
  taggedItems: any[] = [];
  stepItemSearch: string = '';
  stepItemSuggestions: any[] = [];
  stepItemInput$ = new Subject<string>();
  itemGroupList: string[] = [];
  userData: any = null;
  inames: any[] = [];

  // Support documents and hyperlinks (only for cover)
  supportingDocumentDocuments: any[] = [];
  supportingDocumentHyperLinks: any[] = [];
  linkName: string = '';
  hyperLink: string = '';
  documentName: string = '';
  documentFile: File | null = null;
  isUploadingDocument: boolean = false;
  documentUploadProgress: number = 0;

  // Safety critical properties (only for steps)
  isSafetyCritical: boolean = false;
  safetyInstructionsTitle: string = '';
  safetyInstructionsDescription: string = '';

  constructor(
    public dialogRef: MatDialogRef<CaptionDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CaptionDialogData,
    private http: HttpClient,
    private toastr: ToastrService,
    private itemLibraryService: ItemLibraryService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef
  ) {
    this.title = data.title || '';
    this.description = data.description || '';
    this.coverImageUrl = data.posterUrl || '';
    this.taggedItems = data.taggedItems ? [...data.taggedItems] : [];
    this.itemGroupList = data.itemGroupList || [];
    this.userData = data.userData || null;
    this.inames = data.inames || [];
    this.supportingDocumentDocuments = data.supportingDocumentDocuments ? [...data.supportingDocumentDocuments] : [];
    this.supportingDocumentHyperLinks = data.supportingDocumentHyperLinks ? [...data.supportingDocumentHyperLinks] : [];
    this.isSafetyCritical = data.isSafetyCritical || false;
    this.safetyInstructionsTitle = data.safetyInstructionsTitle || '';
    this.safetyInstructionsDescription = data.safetyInstructionsDescription || '';
  }

  ngOnInit() {
    if (this.data.type === 'step') {
      this.setupStepItemSearch();
    }
  }

  ngOnDestroy() {
    this.stepItemInput$.complete();
  }

  setupStepItemSearch() {
    this.stepItemInput$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((term: string) => {
        if (term && term.trim().length >= 2) {
          const userEmail = this.userData?.emailAddress || '';
          const librarySearch = this.itemLibraryService.searchItemLibraries({ 
            itemName: term.trim(),
            userEmail: userEmail
          });
          const userItems = of(
            (this.inames || []).filter(item => 
              (item.itemkey || item.itemName || "").toLowerCase().includes(term.toLowerCase().trim())
            )
          );
          
          return librarySearch.pipe(
            switchMap(libraryResults => {
              return userItems.pipe(
                switchMap(userResults => {
                  const allResults = [...libraryResults, ...userResults];
                  const seen = new Set<string>();
                  const filteredResults = allResults.filter(item => {
                    const itemName = item.itemName || item.itemkey || "";
                    const key = `${itemName}|${item.groupName || ''}|${item.link || item.wheretofindit || ''}`;
                    if (seen.has(key)) return false;
                    seen.add(key);
                    return true;
                  });
                  
                  if (filteredResults.length === 0) {
                    return of([{ 
                      isNewItem: true, 
                      itemName: term.trim(),
                      displayText: `Add "${term.trim()}" as new item`
                    }]);
                  }
                  
                  return of(filteredResults);
                })
              );
            })
          );
        }
        this.stepItemSuggestions = [];
        return of([]);
      }),
      catchError((error) => {
        console.error('Error searching items for step:', error);
        if (this.stepItemSearch && this.stepItemSearch.trim().length >= 2) {
          this.stepItemSuggestions = [{ 
            isNewItem: true, 
            itemName: this.stepItemSearch.trim(),
            displayText: `Add "${this.stepItemSearch.trim()}" as new item`
          }];
        } else {
          this.stepItemSuggestions = [];
        }
        return of(this.stepItemSuggestions);
      })
    ).subscribe((results: any[]) => {
      this.stepItemSuggestions = results || [];
      this.cdr.detectChanges();
    });
  }

  onStepItemSearch(value: string) {
    this.stepItemSearch = value;
    if (value && value.trim().length >= 2) {
      this.stepItemInput$.next(value);
    } else {
      this.stepItemSuggestions = [];
      this.cdr.detectChanges();
    }
  }

  tagItemToStep(item: any) {
    if (!item) return;
    
    if (item.isNewItem) {
      this.openAddNewItemDialog(item.itemName);
      return;
    }
    
    const itemName = item.itemName || item.itemkey || "";
    const isAlreadyTagged = this.taggedItems.some(tagged => 
      (tagged.itemName || tagged.itemkey) === itemName
    );
    
    if (!isAlreadyTagged) {
      const taggedItem: any = {
        itemName: item.itemName || item.itemkey || item.itemKey || "",
        itemKey: item.itemkey || item.itemKey || item.itemName || "",
        groupName: item.groupName || "",
        link: item.link || item.wheretofindit || item.whereToFindIt || "",
        imageUrl: item.imageUrl || item.url || "",
        itemValue: item.itemValue || item.quantity || ""
      };
      
      Object.keys(taggedItem).forEach(key => {
        if (taggedItem[key] === "" || taggedItem[key] === null || taggedItem[key] === undefined) {
          delete taggedItem[key];
        }
      });
      
      this.taggedItems.push(taggedItem);
      this.stepItemSearch = "";
      this.stepItemSuggestions = [];
      this.cdr.detectChanges();
    } else {
      this.toastr.info("Item is already tagged to this step");
    }
  }

  removeTaggedItem(index: number) {
    if (index >= 0 && index < this.taggedItems.length) {
      this.taggedItems.splice(index, 1);
      this.cdr.detectChanges();
    }
  }

  editTaggedItem(item: any, itemIndex: number, event?: Event) {
    if (event) {
      event.stopPropagation();
    }

    const dialogRef = this.dialog.open(ItemDetailsDialogComponent, {
      width: '600px',
      maxWidth: '90vw',
      data: {
        itemName: item.itemName || item.itemkey || item.itemKey || '',
        itemQuantity: item.itemValue || item.quantity || '',
        itemGroupName: item.groupName || '',
        itemWhereToFindIt: item.wheretofindit || item.link || '',
        imageUrl: item.url || item.imageUrl || '',
        itemGroupList: this.itemGroupList,
        readOnly: false
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        const updatedItem = {
          ...item,
          itemName: result.itemName || '',
          itemkey: result.itemName || '',
          itemKey: result.itemName || '',
          itemValue: result.itemQuantity || '',
          quantity: result.itemQuantity || '',
          groupName: result.itemGroupName || '',
          link: result.itemWhereToFindIt || '',
          wheretofindit: result.itemWhereToFindIt || ''
        };

        Object.keys(updatedItem).forEach(key => {
          if (updatedItem[key] === '' || updatedItem[key] === null || updatedItem[key] === undefined) {
            delete updatedItem[key];
          }
        });

        if (this.taggedItems[itemIndex] !== undefined) {
          this.taggedItems[itemIndex] = updatedItem;
        }

        this.cdr.detectChanges();
        this.toastr.success('Tagged item updated successfully');
      }
    });
  }

  openAddNewItemDialog(itemName: string) {
    const dialogRef = this.dialog.open(AddNewItemDialogComponent, {
      width: '600px',
      maxWidth: '90vw',
      data: {
        itemName: itemName || '',
        itemGroupList: this.itemGroupList,
        userData: this.userData
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        const newItem = {
          itemName: result.itemName || '',
          itemkey: result.itemName || '',
          itemKey: result.itemName || '',
          itemValue: result.itemQuantity || '',
          quantity: result.itemQuantity || '',
          groupName: result.itemGroupName || '',
          link: result.itemWhereToFindIt || '',
          wheretofindit: result.itemWhereToFindIt || '',
          imageUrl: result.imageUrl || '',
          url: result.imageUrl || ''
        };

        Object.keys(newItem).forEach(key => {
          if (newItem[key] === '' || newItem[key] === null || newItem[key] === undefined) {
            delete newItem[key];
          }
        });

        if (result.saveToItemsList && this.inames) {
          this.inames.push({
            itemkey: newItem.itemName,
            itemValue: newItem.itemValue || '',
            url: newItem.imageUrl || '',
            wheretofindit: newItem.link || '',
            groupName: newItem.groupName || ''
          });
          localStorage.setItem("ItemsRequired", JSON.stringify(this.inames));
        }

        if (result.saveToLibrary && this.userData) {
          const libraryItem: ItemLibraryModel = {
            itemName: newItem.itemName,
            groupName: newItem.groupName || '',
            link: newItem.link || '',
            imageUrl: newItem.imageUrl || '',
            isPublic: false,
            createdBy: this.userData.emailAddress
          };

          this.itemLibraryService.createItemLibrary(libraryItem).subscribe(
            () => this.toastr.success('Item saved to library successfully'),
            (error) => {
              console.error('Error saving item to library:', error);
              this.toastr.error('Failed to save item to library');
            }
          );
        }

        this.taggedItems.push(newItem);
        this.stepItemSearch = "";
        this.stepItemSuggestions = [];
        this.cdr.detectChanges();
        
        this.toastr.success('Item added successfully');
      }
    });
  }

  openItemDetailsDialog(item: any) {
    this.dialog.open(ItemDetailsDialogComponent, {
      width: '600px',
      maxWidth: '90vw',
      data: {
        itemName: item.itemName || item.itemkey || item.itemKey || '',
        itemQuantity: item.itemValue || item.quantity || '',
        itemGroupName: item.groupName || '',
        itemWhereToFindIt: item.wheretofindit || item.link || '',
        imageUrl: item.url || item.imageUrl || '',
        itemGroupList: this.itemGroupList,
        readOnly: true
      }
    });
  }

  displayItemName(item: any): string {
    if (!item) return '';
    if (typeof item === 'string') return item;
    if (item.isNewItem) return item.displayText || item.itemName || '';
    return item.itemName || item.itemkey || '';
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSave(): void {
    if (this.title.trim()) {
      // Validate safety critical step
      if (this.data.type === 'step' && this.isSafetyCritical) {
        if (!this.safetyInstructionsTitle || this.safetyInstructionsTitle.trim().length === 0) {
          this.toastr.warning("Safety instructions title is required for safety critical steps.");
          return;
        }
        if (!this.safetyInstructionsDescription || this.safetyInstructionsDescription.trim().length === 0) {
          this.toastr.warning("Safety instructions description is required for safety critical steps.");
          return;
        }
      }

      // Validate safety critical cover
      if (this.data.type === 'cover' && this.isSafetyCritical) {
        if (!this.safetyInstructionsTitle || this.safetyInstructionsTitle.trim().length === 0) {
          this.toastr.warning("Safety instructions title is required for safety critical cover.");
          return;
        }
        if (!this.safetyInstructionsDescription || this.safetyInstructionsDescription.trim().length === 0) {
          this.toastr.warning("Safety instructions description is required for safety critical cover.");
          return;
        }
      }

      const result: any = {
        title: this.title.trim(),
        description: this.description.trim(),
        posterUrl: this.coverImageUrl
      };
      
      // Include tagged items for steps
      if (this.data.type === 'step') {
        result.taggedItems = this.taggedItems;
        result.isSafetyCritical = this.isSafetyCritical || false;
        result.safetyInstructionsTitle = this.isSafetyCritical ? (this.safetyInstructionsTitle || "") : "";
        result.safetyInstructionsDescription = this.isSafetyCritical ? (this.safetyInstructionsDescription || "") : "";
      }
      
      // Include support documents, hyperlinks, and safety fields for cover
      if (this.data.type === 'cover') {
        result.supportingDocumentDocuments = this.supportingDocumentDocuments;
        result.supportingDocumentHyperLinks = this.supportingDocumentHyperLinks;
        result.isSafetyCritical = this.isSafetyCritical || false;
        result.safetyInstructionsTitle = this.isSafetyCritical ? (this.safetyInstructionsTitle || "") : "";
        result.safetyInstructionsDescription = this.isSafetyCritical ? (this.safetyInstructionsDescription || "") : "";
      }
      
      this.dialogRef.close(result);
    }
  }

  titleChanged() {
    if (this.title && this.title.length > this.maxtitlelenght) {
      this.title = this.title.substring(0, this.maxtitlelenght);
    }
  }

  onCoverImageSelect(event: any) {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        this.toastr.error('Please select an image file');
        return;
      }
      
      // Validate file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        this.toastr.error('Image size should be less than 2MB');
        return;
      }

      this.coverImageFile = file;
      
      // Preview image
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.coverImageUrl = e.target.result;
      };
      reader.readAsDataURL(file);
      
      // Upload immediately
      this.uploadCoverImage();
    }
  }

  uploadCoverImage() {
    if (!this.coverImageFile) return;

    const formData = new FormData();
    formData.append("fileData", this.coverImageFile);

    this.isUploadingCoverImage = true;
    this.coverImageUploadProgress = 0;

    const uploadUrl = environment.apiBaseUrl + environment.uploadImageUrl;

    this.http
      .post(uploadUrl, formData, {
        reportProgress: true,
        observe: "events",
      })
      .subscribe(
        (event: HttpEvent<any>) => {
          switch (event.type) {
            case HttpEventType.UploadProgress:
              if (event.total) {
                this.coverImageUploadProgress = Math.round(
                  (event.loaded / event.total) * 100
                );
              }
              break;
            case HttpEventType.Response:
              if (event.body && event.body.fileName) {
                this.coverImageUrl = environment.imageStorageBaseUrl + event.body.fileName;
                this.isUploadingCoverImage = false;
                this.toastr.success('Cover image uploaded successfully');
              } else {
                this.isUploadingCoverImage = false;
                this.toastr.error('Failed to upload cover image');
              }
              break;
          }
        },
        (error) => {
          this.isUploadingCoverImage = false;
          this.toastr.error('Failed to upload cover image');
        }
      );
  }

  triggerCoverImageUpload() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e: any) => this.onCoverImageSelect(e);
    input.click();
  }

  removeCoverImage() {
    this.coverImageUrl = '';
    this.coverImageFile = null;
  }

  // Support Documents and Hyperlinks methods (only for cover)
  onDocumentSelect(event: any) {
    const file = event.target.files?.[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        this.toastr.error('Please select a PDF file');
        return;
      }
      
      if (file.size > 10 * 1024 * 1024) {
        this.toastr.error('File size should be less than 10MB');
        return;
      }

      this.documentFile = file;
      this.uploadDocument();
    }
  }

  uploadDocument() {
    if (!this.documentFile) return;

    const formData = new FormData();
    formData.append("fileData", this.documentFile);

    this.isUploadingDocument = true;
    this.documentUploadProgress = 0;

    const uploadUrl = environment.apiBaseUrl + environment.uploadDocumentsUrl;

    this.http
      .post(uploadUrl, formData, {
        reportProgress: true,
        observe: "events",
      })
      .subscribe(
        (event: HttpEvent<any>) => {
          switch (event.type) {
            case HttpEventType.UploadProgress:
              if (event.total) {
                this.documentUploadProgress = Math.round(
                  (event.loaded / event.total) * 100
                );
              }
              break;
            case HttpEventType.Response:
              if (event.body && event.body.fileName) {
                const documentUrl = environment.documentsStorageBaseUrl + event.body.fileName;
                const docName = this.documentName.trim() || this.documentFile?.name || 'Untitled document';
                
                this.addSupportingDocument(docName, documentUrl);
                this.documentFile = null;
                this.documentName = '';
                this.isUploadingDocument = false;
                this.documentUploadProgress = 0;
              } else {
                this.isUploadingDocument = false;
                this.toastr.error('Failed to upload document');
              }
              break;
          }
        },
        (error) => {
          this.isUploadingDocument = false;
          this.toastr.error('Failed to upload document');
        }
      );
  }

  addSupportingDocument(documentName: string, documentUrl: string) {
    if (
      (this.supportingDocumentHyperLinks?.length || 0) +
        (this.supportingDocumentDocuments?.length || 0) >=
      10
    ) {
      this.toastr.warning("Maximum 10 supporting documents allowed");
      return;
    }

    if (!this.supportingDocumentDocuments) {
      this.supportingDocumentDocuments = [];
    }

    this.supportingDocumentDocuments.push({
      documentName: documentName,
      documentUrl: documentUrl,
    });
    
    this.toastr.success("Document added successfully");
    this.cdr.detectChanges();
  }

  removeSupportingDocument(index: number) {
    if (index >= 0 && index < this.supportingDocumentDocuments.length) {
      this.supportingDocumentDocuments.splice(index, 1);
      this.cdr.detectChanges();
    }
  }

  addLink() {
    if (
      (this.supportingDocumentHyperLinks?.length || 0) +
        (this.supportingDocumentDocuments?.length || 0) >=
      10
    ) {
      this.toastr.warning("Maximum 10 supporting documents allowed");
      return;
    }

    if (!this.linkName || !this.hyperLink) {
      this.toastr.warning("Please enter both anchor text and URL");
      return;
    }

    if (!this.supportingDocumentHyperLinks) {
      this.supportingDocumentHyperLinks = [];
    }

    this.supportingDocumentHyperLinks.push({
      linkName: this.linkName,
      linkUrl: this.hyperLink,
    });
    
    this.toastr.success("Link added successfully");
    this.linkName = "";
    this.hyperLink = "";
    this.cdr.detectChanges();
  }

  removeSupportingDocumentHyperLink(index: number) {
    if (index >= 0 && index < this.supportingDocumentHyperLinks.length) {
      this.supportingDocumentHyperLinks.splice(index, 1);
      this.cdr.detectChanges();
    }
  }

  editLink(index: number, link: any) {
    this.linkName = link.linkName || '';
    this.hyperLink = link.linkUrl || '';
    this.removeSupportingDocumentHyperLink(index);
  }

  triggerDocumentUpload() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/pdf';
    input.onchange = (e: any) => this.onDocumentSelect(e);
    input.click();
  }

  truncateText(text: string, maxLength: number): string {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  }
}
